import React, { Component } from 'react';
import {
  hasSubscription, isPastDue, statusLabel, statusVariant,
} from '../../../util/subscriptionStatus';

/**
 * One shop's subscription. Presentational — the parent owns fetching, so the
 * refresh-after-redirect logic lives in one place.
 *
 * Billing is per SHOP (an `installer_location` row), not per installer. An
 * installer owning several shops has several subscriptions, several Stripe
 * customers, and several bank mandates.
 */
class SubscriptionPanel extends Component {
  state = { tier: '', interval: 'month' };

  handleSubscribe = () => {
    const { tier, interval } = this.state;
    if (!tier) return;
    this.props.onSubscribe(tier, interval);
  };

  renderPlanPicker() {
    const { plans = [], busy } = this.props;
    const { tier, interval } = this.state;
    // Only show tiers that have pricing for the currently selected interval
    const tiers = [...new Set(plans.filter(p => p.billing_interval === interval).map(p => p.tier))];

    return (
      <>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label htmlFor="sub-tier">Plan</label>
            {tiers.length === 0 ? (
              <div className="alert alert-warning mb-0 py-2 small">
                No plans are available for this billing interval.
              </div>
            ) : (
              <select
                id="sub-tier" className="form-control" value={tier} disabled={busy}
                onChange={(e) => this.setState({ tier: e.target.value })}
              >
                <option value="">Choose a plan…</option>
                {tiers.map(t => {
                  const row = plans.find(p => p.tier === t && p.billing_interval === interval);
                  const price = row && row.amount_cents != null
                    ? ` — $${(row.amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '';
                  return <option key={t} value={t}>{(row && row.display_name) || t}{price}</option>;
                })}
              </select>
            )}
          </div>
          <div className="form-group col-md-6">
            <label htmlFor="sub-interval">Billing</label>
            <select
              id="sub-interval" className="form-control" value={interval} disabled={busy}
              onChange={(e) => {
                const newInterval = e.target.value;
                // If current tier is not valid for new interval, clear it
                const tierStillValid = plans.some(p => p.tier === tier && p.billing_interval === newInterval);
                this.setState({
                  interval: newInterval,
                  tier: tierStillValid ? tier : '',
                });
              }}
            >
              <option value="month">Monthly</option>
              <option value="year">Annual — two months free</option>
            </select>
          </div>
        </div>

        <button
          className="jv-btn jv-btn-primary"
          onClick={this.handleSubscribe}
          disabled={busy || !tier}
        >
          {busy ? <><i className="fa-sharp fa-solid fa-spinner fa-spin mr-1" />Opening…</> : 'Subscribe'}
        </button>

        <p className="text-muted small mt-3 mb-0">
          Paid by bank debit (ACH) — cards are not accepted. Your bank is verified
          instantly where supported, otherwise by small test deposits over a few days.
          The first charge is 30 days from signup. Have a promo code? Enter it at checkout.
        </p>
      </>
    );
  }

  renderActive() {
    const { subscription, busy } = this.props;
    const tier = subscription.subscription_tier || 'Subscribed';
    const interval = subscription.billing_interval === 'year' ? 'annual' : 'monthly';

    return (
      <>
        <div className="d-flex align-items-center mb-2">
          <h5 className="mb-0 mr-2">{tier}</h5>
          <span className="text-muted">· {interval}</span>
        </div>
        <p className="mb-3">
          <span className={`badge badge-${statusVariant(subscription)}`}>
            {statusLabel(subscription)}
          </span>
        </p>

        {isPastDue(subscription) && (
          <div className="alert alert-danger py-2 small">
            The last payment failed. Update the bank account to restore billing.
            This does not affect your access to Jobvious.
          </div>
        )}

        <button className="jv-btn jv-btn-secondary" onClick={this.props.onManage} disabled={busy}>
          {busy ? <><i className="fa-sharp fa-solid fa-spinner fa-spin mr-1" />Opening…</> : 'Manage payment method & invoices'}
        </button>
      </>
    );
  }

  render() {
    const { location, subscription, loading, error } = this.props;

    return (
      <div className="card mb-4">
        <div className="card-header">
          <h4 className="mb-0">
            <i className="fa-sharp fa-regular fa-crown mr-2" />
            Subscription{location ? ` — ${location.location_name}` : ''}
          </h4>
        </div>
        <div className="card-body">
          {!location && (
            <p className="text-muted mb-0">
              Select a location from the header to manage its subscription.
              Each shop is billed separately.
            </p>
          )}

          {location && loading && (
            <p className="text-muted mb-0">
              <i className="fa-sharp fa-solid fa-spinner fa-spin mr-2" />Loading…
            </p>
          )}

          {location && !loading && error && (
            <div className="alert alert-danger py-2 small">{error}</div>
          )}

          {location && !loading && (
            hasSubscription(subscription) ? this.renderActive() : this.renderPlanPicker()
          )}
        </div>
      </div>
    );
  }
}

export default SubscriptionPanel;
